export const POSITION_TYPES = [
  'supply',
  'borrow',
  'reward',
  'stake',
  // Perps display rows: per-market perp positions and per-token spot holdings.
  // These are rendered for context only — their USD value is excluded from
  // protocol totals (see getPositionTypeMultiplier) so they don't double-count
  // the equity-based `collateral` position that drives the header balance.
  'perp',
  'spot',
  'collateral',
  'collectible',
  // INX allocation source-specific position types
  'inx-patron-nft',
  'inx-patron-sale',
  'inx-sonar-sale',
  'inx-team',
] as const;
export type PositionType = (typeof POSITION_TYPES)[number];

/** INX-specific position types that map to allocation sources */
export const INX_POSITION_TYPES = [
  'inx-patron-nft',
  'inx-patron-sale',
  'inx-sonar-sale',
  'inx-team',
] as const;
export type InxPositionType = (typeof INX_POSITION_TYPES)[number];

export type Protocol = {
  name: string;
  iconUrl: string;
};

/**
 * The multiplier for a position type, to reflect in total usd balance
 *
 * e.g. Supply positions are positive, borrow positions are negative
 */
export const getPositionTypeMultiplier = (
  positionType: PositionType
): number => {
  if (positionType === 'supply') return 1;
  if (positionType === 'borrow') return -1;
  if (positionType === 'reward') return 1;
  if (positionType === 'stake') return 1;
  // Perp/spot rows are display-only; their value is already reflected by the
  // provider's `collateral` equity position, so they must not affect the total.
  if (positionType === 'perp') return 0;
  if (positionType === 'spot') return 0;
  if (positionType === 'collateral') return 1;
  if (positionType === 'collectible') return 1;
  // INX position types - all positive value
  if (positionType === 'inx-patron-nft') return 1;
  if (positionType === 'inx-patron-sale') return 1;
  if (positionType === 'inx-sonar-sale') return 1;
  if (positionType === 'inx-team') return 1;
  throw new Error(`Unknown position type: ${positionType}`);
};

export const getPositionActionName = (positionType: PositionType): string => {
  if (positionType === 'supply') return 'Supplied';
  if (positionType === 'borrow') return 'Borrowed';
  if (positionType === 'reward') return 'Rewarded';
  if (positionType === 'stake') return 'Staked';
  if (positionType === 'perp') return 'Perps';
  if (positionType === 'spot') return 'Spot';
  if (positionType === 'collateral') return 'Collateral';
  if (positionType === 'collectible') return 'Cards';
  // INX position types
  if (positionType === 'inx-patron-nft') return 'Ready to claim';
  if (positionType === 'inx-patron-sale') return 'Patron Sale Locked';
  if (positionType === 'inx-sonar-sale') return 'Sonar Sale Locked';
  if (positionType === 'inx-team') return 'Team Locked';
  throw new Error(`Unknown position type: ${positionType}`);
};

/** Check if a position type is an INX allocation type */
export const isInxPositionType = (
  positionType: PositionType
): positionType is InxPositionType => {
  return INX_POSITION_TYPES.includes(positionType as InxPositionType);
};

export const KNOWN_POSITION_PROTOCOLS = {
  'aave-v3': {
    name: 'Aave V3',
    companyName: 'Aave',
    shouldHideReceipts: true,
    /** NOTE: This should ideally pulled from some connect shared location */
    connectName: 'aave-infinex',
    homepageUrl: 'https://app.aave.com',
    iconUrl:
      'https://res.cloudinary.com/infinex/image/upload/v1780634773/partners/aave-token-round.png',
  },
  'collector-crypt': {
    name: 'Collector Crypt',
    companyName: 'Collector Crypt',
    shouldHideReceipts: true,
    connectName: null,
    homepageUrl: 'https://collectorcrypt.com',
    iconUrl:
      'https://res.cloudinary.com/infinex/image/upload/v1780108743/partners/collector-crypt-logo.png',
  },
  'kaito-staking': {
    name: 'Kaito',
    companyName: 'Kaito',
    shouldHideReceipts: false,
    connectName: null,
    homepageUrl: 'https://www.kaito.ai/',
    iconUrl: 'https://infinex.xyz/assets/partners/kaito-logo.png',
  },
  hyperliquid: {
    name: 'Hyperliquid',
    companyName: 'Hyperliquid',
    shouldHideReceipts: true,
    connectName: null,
    homepageUrl: 'https://hyperliquid.xyz',
    iconUrl:
      'https://res.cloudinary.com/infinex/image/upload/external-dapps/hyperliquid.png',
  },
  synthetix: {
    name: 'Synthetix',
    companyName: 'Synthetix',
    shouldHideReceipts: true,
    connectName: null,
    homepageUrl: 'https://synthetix.io',
    iconUrl:
      'https://res.cloudinary.com/infinex/image/upload/v1780634943/partners/snx-brand.png',
  },
  lighter: {
    name: 'Lighter',
    companyName: 'Lighter',
    shouldHideReceipts: true,
    connectName: null,
    homepageUrl: 'https://lighter.xyz',
    iconUrl:
      'https://res.cloudinary.com/infinex/image/upload/v1768799013/external-dapps/lighter.png',
  },
  'infinex-allocations': {
    name: 'INX',
    companyName: 'Infinex',
    shouldHideReceipts: true,
    connectName: null,
    homepageUrl: '/inx',
    iconUrl:
      'https://res.cloudinary.com/infinex/image/upload/tokens/infinex.svg',
  },
} as const;

export const PROTOCOL_IDS = Object.keys(KNOWN_POSITION_PROTOCOLS) as [
  ProtocolId,
  ...ProtocolId[],
];

export type ProtocolId = keyof typeof KNOWN_POSITION_PROTOCOLS;
